<template>
  <div class="pagination-container">
    <div class="custom-pagination">
      <button class="page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
        <span>&lt;</span>
      </button>
      <button
        v-if="showEllipsisBefore"
        class="page-btn ellipsis"
        @click="handleJump('prev')"
      >...</button>
      <button
        v-for="page in visiblePages"
        :key="page"
        class="page-btn"
        :class="{ active: page === currentPage }"
        @click="changePage(page)"
      >
        {{ page }}
      </button>
      <button
        v-if="showEllipsisAfter"
        class="page-btn ellipsis"
        @click="handleJump('next')"
      >...</button>
      <button
        v-if="lastPageVisible !== totalPages"
        class="page-btn"
        :class="{ active: totalPages === currentPage }"
        @click="changePage(totalPages)"
      >
        {{ totalPages }}
      </button>
      <span class="goto-label">到</span>
      <input
        v-model.number="gotoPage"
        @keyup.enter="handleGotoPage"
        class="goto-input"
        min="1"
        :max="totalPages"
      />
      <span class="goto-label">页</span>
      <select v-model.number="internalPageSize" @change="handleSizeChange($event.target.value)" class="size-select">
        <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}条/页</option>
      </select>
      <span class="stat-info">
        共 <span class="green">{{ totalPages }}</span> 页，
        <span class="green">{{ total }}</span> 条数据
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue'

// 新增滚动方法
const scrollToTop = () => {
  // 方案1：使用平滑滚动到页面顶部
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });

  // 方案2：滚动到自定义容器（如果页面有特定滚动区域）
  const mainContainer = document.querySelector('.el-main');
  if (mainContainer) {
    mainContainer.scrollTop = 0;
  }
};

interface PaginationProps {
  total: number
  currentPage: number
  pageSize: number
  pageSizeOptions?: number[]
}

interface PaginationEmits {
  (e: 'update:currentPage', page: number): void
  (e: 'update:pageSize', size: number): void
  (e: 'change', page: number, size: number): void
}

const props = withDefaults(defineProps<PaginationProps>(), {
  pageSizeOptions: () => [10, 20, 40, 60, 80]
})

const emit = defineEmits<PaginationEmits>()

// 内部状态
const gotoPage = ref(props.currentPage)
const internalPageSize = ref(props.pageSize)

// 计算属性
const totalPages = computed(() => Math.ceil(props.total / internalPageSize.value))

const visiblePages = computed(() => {
  const pages = []
  const maxVisible = 5
  let start = Math.max(1, props.currentPage - 2)
  let end = Math.min(totalPages.value, start + maxVisible - 1)
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1)
  }
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

const lastPageVisible = computed(() => {
  const v = visiblePages.value
  return v.length ? v[v.length - 1] : 1
})

const showEllipsisAfter = computed(() => {
  return lastPageVisible.value < totalPages.value - 1
})

const showEllipsisBefore = computed(() => {
  return visiblePages.value[0] > 2
})

// 方法
const changePage = (page: number) => {
  if (page < 1 || page > totalPages.value || page === props.currentPage) return
  emit('update:currentPage', page)
  emit('change', page, internalPageSize.value)
  gotoPage.value = page

  scrollToTop(); // 新增滚动调用
}

const handleGotoPage = () => {
  let page = gotoPage.value
  if (!page || page < 1) page = 1
  if (page > totalPages.value) page = totalPages.value
  changePage(page)
}

const handleJump = (direction: 'prev' | 'next') => {
  if (direction === 'prev') {
    const target = Math.max(1, visiblePages.value[0] - 2)
    changePage(target)
  } else {
    const target = Math.min(totalPages.value, visiblePages.value[visiblePages.value.length - 1] + 2)
    changePage(target)
  }
}

const handleSizeChange = (val: string) => {
  const newSize = Number(val)
  internalPageSize.value = newSize
  emit('update:pageSize', newSize)
  emit('update:currentPage', 1)
  emit('change', 1, newSize)
  gotoPage.value = 1
}

// 监听 props 变化，同步内部状态
watch(() => props.currentPage, (newPage) => {
  gotoPage.value = newPage
})

watch(() => props.pageSize, (newSize) => {
  internalPageSize.value = newSize
})
</script>

<style lang="scss" scoped>
.pagination-container {
  background: transparent;
  box-shadow: none;
  margin-top: 30px;
  margin-bottom: 10px;
}

.custom-pagination {
  display: flex;
  align-items: center;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 4px;
  font-size: 14px;
  margin: 0;
  width: auto;
  min-width: 0;
  position: relative;
}

.custom-pagination .page-btn {
  width: 38px;
  height: 38px;
  background: #232323 !important;
  color: #6c6a6a !important;
  border: none !important;
  border-radius: 5px !important;
  margin: 0 2px;
  font-size: 17px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom-pagination .page-btn.active {
  background: #01FF95 !important;
  color: #111 !important;
  font-weight: bold;
  box-shadow: 0 0 0 2px #00ff9933;
}

.custom-pagination .page-btn:disabled {
  background: #111 !important;
  color: #444 !important;
  cursor: not-allowed;
}

.custom-pagination .ellipsis {
  color: #888;
  margin: 0 2px;
  font-size: 17px;
  user-select: none;
}

.custom-pagination .goto-label {
  color: #6c6a6a;
  margin: 0 2px;
  font-size: 14px;
}

.custom-pagination .goto-input {
  width: 38px;
  height: 32px;
  background: #232323 !important;
  border: 1px solid #333 !important;
  border-radius: 8px !important;
  color: #6c6a6a !important;
  text-align: center;
  margin: 0 2px;
  font-size: 14px;
  outline: none;
  transition: border 0.2s;
}

.custom-pagination .goto-input:focus {
  border: 1.5px solid #01FF95 !important;
}

.custom-pagination .size-select {
  background: #232323 !important;
  color: #6c6a6a !important;
  border: 1px solid #333 !important;
  border-radius: 8px !important;
  height: 38px;
  margin: 0 8px 0 4px;
  font-size: 14px;
  outline: none;
  padding: 0 8px;
  transition: border 0.2s;
}

.custom-pagination .size-select:focus {
  border: 1.5px solid #01FF95 !important;
}

.custom-pagination .size-select option {
  background: #232323 !important;
  color: #fff !important;
}

.custom-pagination .stat-info {
  color: #6c6a6a;
  font-size: 14px;
  margin-left: 10px;
  letter-spacing: 1px;
}

.custom-pagination .green {
  color: #01FF95;
  font-size: 16px;
}
</style>
